/**
 * Browser selection -> character offsets into the original answer string.
 *
 * The one mapper: every caller that turns a gesture into a range goes through here, so what the
 * confirm bubble promises and what the server stores can never drift apart.
 */

/** A range in the ORIGINAL text: UTF-16 offsets, end exclusive — the wire's shape. */
export interface SurveyBlackoutOffsets {
    start: number
    end: number
}

/** A settled selection: which text container it landed in, what it covers, where to hang the bubble. */
export interface SurveyBlackoutSelection {
    /** The caller's own key for the container the selection was clipped to. */
    key: string
    offsets: SurveyBlackoutOffsets
    /** Viewport rect of the selection's LAST line fragment — see `lastRectOf`. */
    anchor: DOMRect
}

/** One registered text container: the caller's key, and the element rendering that text. */
export interface SurveyBlackoutContainer {
    key: string
    element: HTMLElement | null | undefined
}

/** All of them, in DOM order — the order a selection is resolved against. */
export type SurveyBlackoutContainers = readonly SurveyBlackoutContainer[]

const isInside = (container: HTMLElement, node: Node | null) =>
    !!node && (node === container || container.contains(node));

/**
 * The live selection, clipped to one container.
 *
 * A drag that starts or ends outside the answer keeps the container's own boundary on that side,
 * and a selection that never touches the container is a no-op (null). A drag across two answers
 * therefore resolves to the part inside the first container it touches — and because the bubble
 * hangs off that clipped range, it appears exactly where the blackout will actually stop.
 */
const clippedSelectionRange = (container: HTMLElement): Range | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;

    const range = selection.getRangeAt(0);
    if (!range.intersectsNode(container)) return null;

    const clipped = document.createRange();
    clipped.selectNodeContents(container);
    if (isInside(container, range.startContainer)) clipped.setStart(range.startContainer, range.startOffset);
    if (isInside(container, range.endContainer)) clipped.setEnd(range.endContainer, range.endOffset);

    return clipped.collapsed ? null : clipped;
};

/**
 * A selection spans one client rect per line fragment. The LAST one is the far end of the
 * selection — for a normal left-to-right drag, the line the pointer was released on — so hanging
 * the confirm bubble off it puts the button where the gesture just ended instead of somewhere up
 * in the middle of a multi-line block.
 */
const lastRectOf = (range: Range): DOMRect => {
    const rects = range.getClientRects();
    return rects.item(rects.length - 1) || range.getBoundingClientRect();
};

/** Live anchor rect, re-read on demand so the bubble keeps tracking the text while it moves. */
export const surveyBlackoutSelectionRect = (container: HTMLElement | null | undefined): DOMRect | null => {
    if (!container) return null;
    const range = clippedSelectionRange(container);
    return range ? lastRectOf(range) : null;
};

/**
 * Selection -> offsets, plus the bubble anchor, for the first container the selection touches.
 *
 * Offsets are `Range.toString().length`, which counts the source text of the DOM text nodes — so
 * any range works: mid-word, a single character, or across newlines. Emoji count as their UTF-16
 * length here, in `String.prototype.slice` and on the server, so every side stays in step. Offsets
 * and anchor come from the same clipped range in one pass, so the bubble can never end up
 * describing a different range than the one it applies.
 */
export const readSurveyBlackoutSelection = (
    containers: SurveyBlackoutContainers
): SurveyBlackoutSelection | null => {
    for (const {key, element} of containers) {
        if (!element) continue;

        const range = clippedSelectionRange(element);
        if (!range) continue;

        const head = document.createRange();
        head.selectNodeContents(element);
        head.setEnd(range.startContainer, range.startOffset);

        const start = head.toString().length;
        const end = start + range.toString().length;
        if (end <= start) continue;

        return {key, offsets: {start, end}, anchor: lastRectOf(range)};
    }

    return null;
};

export const hasActiveTextSelection = () => {
    const selection = window.getSelection();
    return !!selection && !selection.isCollapsed;
};

export const clearTextSelection = () => window.getSelection()?.removeAllRanges();
